import { useState, useEffect } from 'react';
import { guildsAPI } from '../../api';
import ChannelSelect from '../../components/ui/ChannelSelect';
import api from '../../api';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris/Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

function Section({ title, description, children }) {
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: '16px', padding: '24px', boxShadow: 'var(--shadow-card)',
      marginBottom: '20px',
    }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)', marginBottom: '4px' }}>{title}</h2>
      {description && <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>{description}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: description ? 0 : '16px' }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
        {label}
      </label>
      {children}
      {hint && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{hint}</div>}
    </div>
  );
}

export default function ConfigPage({ guild, setGuild }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reports, setReports] = useState({ scheduled_reports_enabled: false, reports_channel_id: '', reports_day: 1, reports_hour: 9 });
  const [leaderboard, setLeaderboard] = useState({ leaderboard_channel_id: '', leaderboard_public: false });
  const [pushingLeaderboard, setPushingLeaderboard] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    api.get(`/api/reports/${guild.id}`).then(r => setReports(r.data)).catch(() => {});
    api.get(`/api/leaderboard/${guild.id}`).then(r => setLeaderboard(r.data)).catch(() => {});
    guildsAPI.getOne(guild.id)
      .then(res => {
        setConfig(res.data);
        setForm({
          currency_name: res.data.currency_name || 'Dollar',
          currency_symbol: res.data.currency_symbol || '$',
          timezone: res.data.timezone || 'America/New_York',
          reset_every: res.data.reset_interval?.split('_')[0] || '2',
          reset_unit: res.data.reset_interval?.split('_')[1] || 'weeks',
          reset_hour: res.data.reset_hour ?? 0,
          min_hours: res.data.min_hours ?? 0,
          max_hours: res.data.max_hours ?? 0,
          bonus_enabled: res.data.bonus_enabled ?? false,
          bonus_type: res.data.bonus_type || 'flat',
          bonus_amount: res.data.bonus_amount ?? 0,
          bonus_cap: res.data.bonus_cap ?? 0,
          shift_notes_enabled: res.data.shift_notes_enabled ?? true,
          clockin_cooldown_minutes: res.data.clockin_cooldown_minutes ?? 2,
          shift_limit_hours: res.data.shift_limit_hours ?? 8,
          leaderboard_public: res.data.leaderboard_public ?? false,
          birthday_bonus_enabled: res.data.birthday_bonus_enabled ?? false,
          birthday_bonus_amount: res.data.birthday_bonus_amount ?? 0,
          birthday_channel_id: res.data.birthday_channel_id || '',
          loa_channel_id: res.data.loa_channel_id || '',
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [guild.id]);

  const handleSaveReports = async (newReports) => {
    try {
      await api.patch(`/api/reports/${guild.id}`, newReports);
      setReports(newReports);
    } catch { alert('Failed to save report settings.'); }
  };

  const handleSaveLeaderboard = async (newLeaderboard) => {
    try {
      await api.patch(`/api/leaderboard/${guild.id}`, newLeaderboard);
      setLeaderboard(newLeaderboard);
    } catch { alert('Failed to save leaderboard settings.'); }
  };

  const handlePushLeaderboard = async () => {
    if (!leaderboard.leaderboard_channel_id) return alert('Please select a leaderboard channel first.');
    setPushingLeaderboard(true);
    try {
      await api.post(`/api/leaderboard/${guild.id}/push`);
      alert('Leaderboard posted to Discord!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to push leaderboard.');
    } finally {
      setPushingLeaderboard(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        currency_name: form.currency_name,
        currency_symbol: form.currency_symbol,
        timezone: form.timezone,
        reset_interval: `${form.reset_every}_${form.reset_unit}`,
        reset_hour: parseInt(form.reset_hour),
        min_hours: parseFloat(form.min_hours),
        max_hours: parseFloat(form.max_hours),
        bonus_enabled: form.bonus_enabled,
        bonus_type: form.bonus_type,
        bonus_amount: parseFloat(form.bonus_amount),
        bonus_cap: parseFloat(form.bonus_cap),
        shift_notes_enabled: form.shift_notes_enabled,
        clockin_cooldown_minutes: parseInt(form.clockin_cooldown_minutes),
        shift_limit_hours: parseInt(form.shift_limit_hours),
        leaderboard_public: form.leaderboard_public,
        birthday_bonus_enabled: form.birthday_bonus_enabled,
        birthday_bonus_amount: parseFloat(form.birthday_bonus_amount) || 0,
        birthday_channel_id: form.birthday_channel_id || null,
        loa_channel_id: form.loa_channel_id || null,
      };
      const res = await guildsAPI.update(guild.id, payload);
      setConfig(res.data);
      if (setGuild) setGuild(prev => ({ ...prev, ...res.data }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert('Failed to save. Please try again.'); }
    finally { setSaving(false); }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
    border: '2px solid var(--border)', borderRadius: '10px',
    fontSize: '14px', fontFamily: 'var(--font-body)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
    transition: 'border-color 0.2s',
  };

  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading config...
    </div>
  );

  return (
    <div style={{ maxWidth: '800px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Config</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>Server settings for {guild.name}</p>
        </div>
        <button onClick={handleSave} disabled={saving} style={{
          padding: '12px 28px', background: saved ? 'var(--success)' : 'var(--gradient-accent)',
          color: '#fff', border: 'none', borderRadius: '12px',
          fontSize: '15px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
          fontFamily: 'var(--font-display)', boxShadow: 'var(--shadow)',
          transition: 'background 0.3s',
        }}>
          {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Changes'}
        </button>
      </div>

      {/* Currency */}
      <Section title="💰 Currency" description="Set the name and symbol used for all pay displays.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Field label="Currency Name" hint='e.g. Dollar, Caps, Gold'>
            <input type="text" value={form.currency_name} onChange={e => update('currency_name', e.target.value)}
              style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </Field>
          <Field label="Currency Symbol" hint='e.g. $, ¢, 💰'>
            <input type="text" value={form.currency_symbol} onChange={e => update('currency_symbol', e.target.value)}
              style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </Field>
        </div>
      </Section>

      {/* Pay Period */}
      <Section title="🔄 Pay Period Reset" description="Configure how often the period resets and at what time.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
          <Field label="Every">
            <input type="number" min="1" max="365" value={form.reset_every} onChange={e => update('reset_every', e.target.value)}
              style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </Field>
          <Field label="Unit">
            <select value={form.reset_unit} onChange={e => update('reset_unit', e.target.value)} style={inputStyle}>
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
            </select>
          </Field>
          <Field label="Reset Hour (0-23)" hint="0 = midnight">
            <input type="number" min="0" max="23" value={form.reset_hour} onChange={e => update('reset_hour', e.target.value)}
              style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </Field>
          <Field label="Timezone">
            <select value={form.timezone} onChange={e => update('timezone', e.target.value)} style={inputStyle}>
              {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
            </select>
          </Field>
        </div>
        {config?.next_reset && (
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', background: 'var(--bg-secondary)', padding: '10px 14px', borderRadius: '8px' }}>
            📅 Next reset: <strong>{new Date(config.next_reset).toLocaleString()}</strong>
          </div>
        )}
      </Section>

      {/* Hours */}
      <Section title="⏱️ Hours Requirements" description="Set minimum and maximum hours per pay period.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Field label="Minimum Hours" hint="0 = no minimum requirement">
            <input type="number" min="0" value={form.min_hours} onChange={e => update('min_hours', e.target.value)}
              style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </Field>
          <Field label="Maximum Hours (Bonus Threshold)" hint="0 = no threshold">
            <input type="number" min="0" value={form.max_hours} onChange={e => update('max_hours', e.target.value)}
              style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </Field>
        </div>
      </Section>

      {/* Bonus System */}
      <Section title="🎁 Bonus System" description="Configure automatic bonuses when staff hit the max hours threshold.">
        <Field label="Bonus System">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={() => update('bonus_enabled', !form.bonus_enabled)} style={{
              width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
              background: form.bonus_enabled ? 'var(--success)' : 'var(--border)',
              position: 'relative', transition: 'background 0.2s',
            }}>
              <div style={{
                width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                position: 'absolute', top: '3px',
                left: form.bonus_enabled ? '25px' : '3px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </button>
            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              {form.bonus_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </Field>
        {form.bonus_enabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <Field label="Bonus Type">
              <select value={form.bonus_type} onChange={e => update('bonus_type', e.target.value)} style={inputStyle}>
                <option value="flat">Flat Amount</option>
                <option value="percent">Percentage of Earnings</option>
              </select>
            </Field>
            <Field label={`Bonus Amount (${form.bonus_type === 'percent' ? '%' : guild?.currency_symbol || '$'})`}>
              <input type="number" min="0" value={form.bonus_amount} onChange={e => update('bonus_amount', e.target.value)}
                style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </Field>
            <Field label="Bonus Cap (0 = no cap)" hint="Max bonus per period">
              <input type="number" min="0" value={form.bonus_cap} onChange={e => update('bonus_cap', e.target.value)}
                style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </Field>
          </div>
        )}
      </Section>

      {/* Dashboard Admins */}
      <Section title="👥 Dashboard Admins" description="Control who can log into this server's web dashboard.">
        <DashboardAdminsSection guild={guild} />
      </Section>

      {/* Shift Settings */}
      <Section title="🕐 Shift Settings" description="Configure shift behavior and warnings.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Field label="Clock-In Cooldown (minutes)" hint="How long before re-clocking in is allowed">
            <input type="number" min="0" value={form.clockin_cooldown_minutes} onChange={e => update('clockin_cooldown_minutes', e.target.value)}
              style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </Field>
          <Field label="Shift Limit Warning (hours)" hint="DM staff after this many hours clocked in. 0 = disabled">
            <input type="number" min="0" value={form.shift_limit_hours} onChange={e => update('shift_limit_hours', e.target.value)}
              style={inputStyle} onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Field label="Shift Notes">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => update('shift_notes_enabled', !form.shift_notes_enabled)} style={{
                width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
                background: form.shift_notes_enabled ? 'var(--success)' : 'var(--border)',
                position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: '3px',
                  left: form.shift_notes_enabled ? '25px' : '3px',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </button>
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                {form.shift_notes_enabled ? 'Enabled (popup on clock-out)' : 'Disabled'}
              </span>
            </div>
          </Field>
          <Field label="Leaderboard">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => update('leaderboard_public', !form.leaderboard_public)} style={{
                width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
                background: form.leaderboard_public ? 'var(--success)' : 'var(--border)',
                position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: '3px',
                  left: form.leaderboard_public ? '25px' : '3px',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </button>
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                {form.leaderboard_public ? 'Public (all members)' : 'Admin only'}
              </span>
            </div>
          </Field>
        </div>
      </Section>

      {/* Scheduled Reports */}
      <Section title="📊 Scheduled Reports" description="Automatically post a weekly summary to a Discord channel.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', background: 'var(--bg-secondary)', borderRadius: '10px' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Enable Weekly Reports</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Post a summary every week to your chosen channel</div>
            </div>
            <button onClick={() => handleSaveReports({ ...reports, scheduled_reports_enabled: !reports.scheduled_reports_enabled })}
              style={{
                width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
                background: reports.scheduled_reports_enabled ? 'var(--success)' : 'var(--border)',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
              }}>
              <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: reports.scheduled_reports_enabled ? '25px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </button>
          </div>

          {reports.scheduled_reports_enabled && (
            <>
              <div>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Report Channel</label>
                <ChannelSelect guildId={guild.id} value={reports.reports_channel_id}
                  onChange={val => handleSaveReports({ ...reports, reports_channel_id: val })}
                  placeholder="Select channel..." />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Day of Week</label>
                  <select value={reports.reports_day}
                    onChange={e => handleSaveReports({ ...reports, reports_day: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }}>
                    {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d,i) => (
                      <option key={i} value={i}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Time (server hour)</label>
                  <select value={reports.reports_hour}
                    onChange={e => handleSaveReports({ ...reports, reports_hour: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }}>
                    {Array.from({length: 24}, (_,i) => (
                      <option key={i} value={i}>{i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i-12}:00 PM`}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      </Section>

      {/* Leaderboard Panel */}
      <Section title="🏆 Leaderboard Panel" description="Post and update a live leaderboard embed in your Discord server.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', background: 'var(--bg-secondary)', borderRadius: '10px' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Public Leaderboard</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Allow any server member to run /leaderboard, not just admins</div>
            </div>
            <button onClick={() => update('leaderboard_public', !form.leaderboard_public)}
              style={{
                width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
                background: form.leaderboard_public ? 'var(--success)' : 'var(--border)',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
              }}>
              <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: form.leaderboard_public ? '25px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </button>
          </div>

          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Leaderboard Channel</label>
            <ChannelSelect
              guildId={guild.id}
              value={leaderboard.leaderboard_channel_id || ''}
              onChange={val => handleSaveLeaderboard({ ...leaderboard, leaderboard_channel_id: val })}
              placeholder="Select channel..."
            />
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
              The leaderboard panel will be posted here when you push it
            </div>
          </div>

          <button
            onClick={handlePushLeaderboard}
            disabled={pushingLeaderboard || !leaderboard.leaderboard_channel_id}
            style={{
              padding: '12px 24px', width: 'fit-content',
              background: (!leaderboard.leaderboard_channel_id || pushingLeaderboard) ? 'var(--text-muted)' : 'var(--gradient-accent)',
              color: '#fff', border: 'none', borderRadius: '10px',
              fontSize: '14px', fontWeight: 700, fontFamily: 'var(--font-display)',
              cursor: (!leaderboard.leaderboard_channel_id || pushingLeaderboard) ? 'not-allowed' : 'pointer',
            }}>
            {pushingLeaderboard ? 'Pushing...' : '📤 Push Leaderboard to Discord'}
          </button>

          <div style={{ fontSize: '12px', color: 'var(--text-muted)', background: 'var(--bg-secondary)', padding: '10px 14px', borderRadius: '8px' }}>
            💡 If a leaderboard panel already exists in the channel, pushing will update it. Staff can also run <strong>/leaderboard</strong> for an on-demand snapshot.
          </div>
        </div>
      </Section>

      {/* Request Notifications */}
      <Section title="📨 Request Notifications" description="Set where LOA and time adjustment requests are posted for admin review.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>LOA Request Channel</label>
            <ChannelSelect
              guildId={guild.id}
              value={form.loa_channel_id || ''}
              onChange={val => update('loa_channel_id', val)}
              placeholder="Select channel..."
            />
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
              LOA requests submitted via the clock-in panel will be posted here with Approve/Deny buttons. Falls back to admin channel if not set.
            </div>
          </div>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: 'var(--text-muted)' }}>
            💡 Time adjustment requests use the <strong>Adjustment Channel</strong> set via <code>/setchannel</code>. LOA requests use the channel above.
          </div>
        </div>
      </Section>

      {/* Birthday Bonuses */}
      <Section title="🎂 Birthday Bonuses" description="Automatically celebrate staff birthdays and optionally grant a bonus.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', background: 'var(--bg-secondary)', borderRadius: '10px' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Enable Birthday Bonuses</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Automatically grant a bonus on staff birthdays</div>
            </div>
            <button onClick={() => update('birthday_bonus_enabled', !form.birthday_bonus_enabled)}
              style={{
                width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
                background: form.birthday_bonus_enabled ? 'var(--success)' : 'var(--border)',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
              }}>
              <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: form.birthday_bonus_enabled ? '25px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </button>
          </div>

          {form.birthday_bonus_enabled && (
            <Field label={`Bonus Amount (${guild?.currency_symbol || '$'})`} hint="Set to 0 for announcements only with no bonus">
              <input type="number" min="0" value={form.birthday_bonus_amount}
                onChange={e => update('birthday_bonus_amount', e.target.value)}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </Field>
          )}

          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Birthday Announcement Channel</label>
            <ChannelSelect
              guildId={guild.id}
              value={form.birthday_channel_id || ''}
              onChange={val => update('birthday_channel_id', val)}
              placeholder="Select channel..."
            />
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Birthday announcements will post here. Staff birthdays are set from their profile on the Staff page.
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

// Dashboard Admins section — exported separately and added to ConfigPage
export function DashboardAdminsSection({ guild }) {
  const [data, setData] = useState({ admins: [], strict_mode: false });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ discord_id: '', username: '', permission_level: 'manager' });
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reports, setReports] = useState({ scheduled_reports_enabled: false, reports_channel_id: '', reports_day: 1, reports_hour: 9 });

  useEffect(() => {
    api.get(`/api/dashboard-admins/${guild.id}`)
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [guild.id]);

  const handleAdd = async () => {
    if (!form.discord_id.trim()) return alert('Discord ID required.');
    setAdding(true);
    try {
      const res = await api.post(`/api/dashboard-admins/${guild.id}`, form);
      setData(prev => ({ ...prev, admins: [res.data, ...prev.admins] }));
      setForm({ discord_id: '', username: '', permission_level: 'manager' });
    } catch { alert('Failed to add admin.'); }
    finally { setAdding(false); }
  };

  const handleRemove = async (discordId) => {
    if (!window.confirm('Remove this dashboard admin?')) return;
    try {
      await api.delete(`/api/dashboard-admins/${guild.id}/${discordId}`);
      setData(prev => ({ ...prev, admins: prev.admins.filter(a => a.discord_id !== discordId) }));
    } catch { alert('Failed to remove admin.'); }
  };

  const handleStrictMode = async (val) => {
    setSaving(true);
    try {
      await api.patch(`/api/dashboard-admins/${guild.id}/strict-mode`, { strict_mode: val });
      setData(prev => ({ ...prev, strict_mode: val }));
    } catch { alert('Failed to update strict mode.'); }
    finally { setSaving(false); }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
    border: '2px solid var(--border)', borderRadius: '10px',
    fontSize: '14px', fontFamily: 'var(--font-body)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Loading...</div>;

  return (
    <div>
      {/* Strict mode toggle */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 16px', background: data.strict_mode ? 'rgba(232,52,28,0.08)' : 'var(--bg-secondary)',
        borderRadius: '12px', marginBottom: '20px',
        border: `1px solid ${data.strict_mode ? 'var(--accent-primary)' : 'var(--border)'}`,
      }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>🔒 Strict Mode</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {data.strict_mode
              ? 'Only manually added admins below can access this dashboard'
              : 'Anyone with Manage Server in Discord can access. Plus anyone added below.'}
          </div>
        </div>
        <button onClick={() => handleStrictMode(!data.strict_mode)} disabled={saving} style={{
          width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
          background: data.strict_mode ? 'var(--accent-primary)' : 'var(--border)',
          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        }}>
          <div style={{
            width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
            position: 'absolute', top: '3px',
            left: data.strict_mode ? '25px' : '3px',
            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </button>
      </div>

      {/* Add admin form */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px auto', gap: '10px', marginBottom: '16px', alignItems: 'end' }}>
        <div>
          <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '5px' }}>Discord ID</label>
          <input type="text" placeholder="123456789012345678"
            value={form.discord_id} onChange={e => setForm(p => ({ ...p, discord_id: e.target.value }))}
            style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '5px' }}>Username (optional)</label>
          <input type="text" placeholder="Username"
            value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
            style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '5px' }}>Permission</label>
          <select value={form.permission_level} onChange={e => setForm(p => ({ ...p, permission_level: e.target.value }))} style={inputStyle}>
            <option value="view_only">View Only</option>
            <option value="manager">Manager</option>
            <option value="full_admin">Full Admin</option>
          </select>
        </div>
        <button onClick={handleAdd} disabled={adding} style={{
          padding: '10px 16px', background: 'var(--gradient-accent)', color: '#fff',
          border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700,
          cursor: adding ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-display)',
          whiteSpace: 'nowrap',
        }}>
          {adding ? '...' : '+ Add'}
        </button>
      </div>

      {/* Admin list */}
      {data.admins.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '14px', background: 'var(--bg-secondary)', borderRadius: '10px' }}>
          No dashboard admins added yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {data.admins.map(admin => (
            <div key={admin.discord_id} style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: '10px',
              border: '1px solid var(--border)',
            }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                background: 'var(--gradient-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', fontWeight: 700, color: '#fff',
              }}>
                {(admin.username || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{admin.username || 'Unknown'}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{admin.discord_id}</div>
              </div>
              <span style={{
                fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '100px',
                background: 'var(--accent-soft)', color: 'var(--accent-primary)',
              }}>
                {admin.permission_level === 'view_only' ? '👁 View Only' : admin.permission_level === 'full_admin' ? '⭐ Full Admin' : '🔧 Manager'}
              </span>
              <button onClick={() => handleRemove(admin.discord_id)} style={{
                padding: '6px 12px', borderRadius: '7px', cursor: 'pointer',
                border: '1px solid var(--danger)', background: 'transparent',
                fontSize: '12px', color: 'var(--danger)', fontFamily: 'var(--font-body)',
              }}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
